import { useState, type FormEvent } from 'react'
import { Icon } from './Icon'
import { API, linkWhatsApp } from '../config'

type Campos = {
  nome: string
  restaurante: string
  whatsapp: string
  canais: string
  objetivo: string
}

const VAZIO: Campos = { nome: '', restaurante: '', whatsapp: '', canais: '', objetivo: '' }

const CANAIS = ['iFood', '99 Food', 'iFood e 99 Food', 'Só no salão / WhatsApp', 'Ainda não vendo por delivery']
const OBJETIVOS = ['Estruturar minha loja', 'Gestão contínua', 'Cardápio digital e automações', 'Aumentar pedidos', 'Ainda não sei, quero orientação']

/** (41) 99999-9999 — o 9 extra do celular muda onde entra o hifen. */
function mascaraTelefone(valor: string): string {
  const d = valor.replace(/\D/g, '').slice(0, 11)
  if (d.length <= 2) return d
  const corte = d.length > 10 ? 7 : 6
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`
  return `(${d.slice(0, 2)}) ${d.slice(2, corte)}-${d.slice(corte)}`
}

/** Os dados do formulario viram uma mensagem pronta de WhatsApp. */
function mensagemWhats(c: Campos): string {
  return [
    'Olá, Skyenex! Vim pelo site.',
    '',
    `Nome: ${c.nome}`,
    `Restaurante: ${c.restaurante}`,
    `WhatsApp: ${c.whatsapp}`,
    `Vendo hoje em: ${c.canais}`,
    `Procuro: ${c.objetivo}`,
  ].join('\n')
}

type Estado =
  | { tipo: 'parado' }
  | { tipo: 'enviando' }
  | { tipo: 'ok'; whats: string }
  | { tipo: 'erro'; texto: string; whats: string }

export function ContactForm() {
  const [campos, setCampos] = useState<Campos>(VAZIO)
  const [armadilha, setArmadilha] = useState('')
  const [invalidos, setInvalidos] = useState<Set<keyof Campos>>(new Set())
  const [estado, setEstado] = useState<Estado>({ tipo: 'parado' })

  const mudar = (chave: keyof Campos) => (e: { target: { value: string } }) => {
    const valor = chave === 'whatsapp' ? mascaraTelefone(e.target.value) : e.target.value
    setCampos((atual) => ({ ...atual, [chave]: valor }))
    if (invalidos.has(chave) && valor.trim()) {
      setInvalidos((atual) => {
        const novo = new Set(atual)
        novo.delete(chave)
        return novo
      })
    }
  }

  async function enviar(e: FormEvent) {
    e.preventDefault()

    const faltando = (Object.keys(VAZIO) as Array<keyof Campos>).filter((k) => !campos[k].trim())
    if (faltando.length) {
      setInvalidos(new Set(faltando))
      setEstado({ tipo: 'erro', texto: 'Preencha todos os campos para continuar.', whats: '' })
      document.querySelector<HTMLElement>(`[name="${faltando[0]}"]`)?.focus()
      return
    }

    // Montado antes do envio: no sucesso o formulario e limpo, e depois disso
    // os campos estariam vazios e a mensagem sairia em branco.
    const whats = linkWhatsApp(mensagemWhats(campos))
    setEstado({ tipo: 'enviando' })

    try {
      const resposta = await fetch(`${API}/contato`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...campos, site: armadilha }),
      })
      const dados = await resposta.json().catch(() => ({ ok: false }))

      if (dados?.ok) {
        setCampos(VAZIO)
        setEstado({ tipo: 'ok', whats })
      } else {
        setEstado({ tipo: 'erro', texto: dados?.erro || 'Não foi possível enviar.', whats })
      }
    } catch {
      setEstado({ tipo: 'erro', texto: 'Não foi possível enviar.', whats })
    }
  }

  const enviando = estado.tipo === 'enviando'

  return (
    <form className="form" id="form-contato" noValidate onSubmit={enviar}>
      <label>Nome
        <input type="text" name="nome" placeholder="Seu nome" value={campos.nome}
          onChange={mudar('nome')} aria-invalid={invalidos.has('nome')} required />
      </label>
      <label>Nome do restaurante
        <input type="text" name="restaurante" placeholder="Como se chama seu restaurante?"
          value={campos.restaurante} onChange={mudar('restaurante')} aria-invalid={invalidos.has('restaurante')} required />
      </label>
      <label>WhatsApp
        <input type="tel" name="whatsapp" placeholder="(00) 00000-0000" value={campos.whatsapp}
          onChange={mudar('whatsapp')} aria-invalid={invalidos.has('whatsapp')} required />
      </label>
      <label>Onde você vende hoje?
        <select name="canais" value={campos.canais} onChange={mudar('canais')} aria-invalid={invalidos.has('canais')} required>
          <option value="">Selecione...</option>
          {CANAIS.map((c) => <option key={c}>{c}</option>)}
        </select>
      </label>
      <label className="form__wide">O que você procura?
        <select name="objetivo" value={campos.objetivo} onChange={mudar('objetivo')} aria-invalid={invalidos.has('objetivo')} required>
          <option value="">Selecione...</option>
          {OBJETIVOS.map((o) => <option key={o}>{o}</option>)}
        </select>
      </label>

      {/* Armadilha para robos: fica fora da tela e pessoa nenhuma preenche.
          Se vier com conteudo, o servidor descarta o envio. */}
      <input type="text" name="site" tabIndex={-1} autoComplete="off" aria-hidden="true"
        className="form__trap" value={armadilha} onChange={(e) => setArmadilha(e.target.value)} />

      <button type="submit" className="btn btn--primary form__submit" disabled={enviando}>
        {enviando ? 'Enviando...' : <>Quero falar com a Skyenex <span className="arw">&rarr;</span></>}
      </button>

      <p className="form__note">
        <Icon name="doc" /> Seus dados estão seguros. Não compartilhamos com terceiros.
      </p>

      <p className={`form__status${estado.tipo === 'ok' ? ' is-ok' : estado.tipo === 'erro' ? ' is-err' : ''}`}
         id="form-status" role="status">
        {estado.tipo === 'ok' && (
          <>
            Recebemos seu contato. Nossa equipe responde em breve.
            <a className="form__wa" href={estado.whats} target="_blank" rel="noopener">
              <Icon name="wa" /> Continuar no WhatsApp
            </a>
          </>
        )}
        {estado.tipo === 'erro' && (
          <>
            {estado.texto}
            {estado.whats && (
              <a className="form__wa" href={estado.whats} target="_blank" rel="noopener">
                <Icon name="wa" /> Falar pelo WhatsApp
              </a>
            )}
          </>
        )}
      </p>
    </form>
  )
}
